import { Suspense, useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import { useActiveCatalog } from '../../hooks/useActiveCatalog';
import { EMPTY_WORKSPACE_SLICE, useWorkspaceStore } from '../../store/workspaceStore';
import { Spinner } from '../ui/Spinner';
import { WorkspaceShell } from './WorkspaceShell';
import { entityPlural } from './entityTerms';
import type { CatalogWorkspaceContext } from './catalogWorkspaceContext';
import { boardCanvasView, isCoreCanvasView, type CanvasView } from '../../types';
import { canvasViewPath } from './canvasNav';
import { FONT } from '../../theme/tokens';

/**
 * SC-2457 → the canvas cutover (SC-2671) — the catalog workspace shell. Mounted at
 * `/catalogs/:id`, it resolves the active catalog once (gating loading, redirecting
 * when there's no catalog or the :id is unknown) and renders the three-panel
 * `WorkspaceShell` (section tabs over the canvas ∣ permanent ChatPanel), handing the
 * catalog to the section routes via the <Outlet> context so each view renders against
 * a guaranteed-present catalog without re-running the guard.
 *
 * Views are URL-driven: the active view is the last path segment and selecting one
 * navigates, so sections deep-link, survive reload, and respond to browser
 * back/forward. The URL speaks the generic canvas view ids (SC-2670).
 */
export function CatalogLayout() {
  const { id = '' } = useParams<{ id: string }>();
  const { active, catalogs, isLoading } = useActiveCatalog();
  const navigate = useNavigate();
  const location = useLocation();

  // The active view is the last path segment (…/history → 'history'), or a
  // board view for the two-segment `boards/:boardId` form (SC-2872 — the URL
  // speaks `boards/<id>`, the store speaks `board:<id>`). Default to the
  // entities grid for the bare `/catalogs/:id` (whose index route redirects
  // here) or an unknown segment.
  const viewMatch = useMatch('/catalogs/:id/:view');
  const boardMatch = useMatch('/catalogs/:id/boards/:boardId');
  // Router params arrive already percent-decoded — decoding again would throw
  // a URIError on ids containing a literal `%`.
  const activeView: CanvasView = boardMatch?.params.boardId
    ? boardCanvasView(boardMatch.params.boardId)
    : isCoreCanvasView(viewMatch?.params.view)
      ? viewMatch.params.view
      : 'entities';

  const resolvedCatalogId = active?.id;

  // The active view's agent-nav provenance — drives the frame ProvenanceBanner (P4,
  // SC-2663). `null` unless an agent open_view navigated to a non-entities view.
  const provenance = useWorkspaceStore((s) =>
    resolvedCatalogId ? s.byCatalog[resolvedCatalogId]?.provenance ?? null : null,
  );
  // The agent seam's store→URL nav request (bumped by applyCanvasDirective).
  const lastNav = useWorkspaceStore((s) => s.lastNav);

  // E1 — URL → store mirror (Canvas P4, SC-2663). A user nav / deep link /
  // back-forward changed the URL: reconcile the store's activeView to it, which
  // CLEARS provenance (a user-reached view is clean). Reads the store via
  // getState() so it sees the fresh value the agent seam just wrote — the echo of
  // an agent-driven store→URL push (E2) lands here with url === store, so it takes
  // no action and the agent's banner survives (the P4 gotcha). On mount / catalog
  // switch it syncs the store to the URL, so a deep link lands clean (provenance
  // isn't in the URL).
  useEffect(() => {
    if (!resolvedCatalogId) return;
    const store = useWorkspaceStore.getState();
    const storeView = (store.byCatalog[resolvedCatalogId] ?? EMPTY_WORKSPACE_SLICE).activeView;
    if (storeView !== activeView) store.navigate(resolvedCatalogId, activeView);
  }, [resolvedCatalogId, activeView]);

  // E2 — store → URL (Canvas P4, SC-2663; SC-2766 adds the query string). The agent seam bumps
  // `lastNav.seq` to request a view (+ for URL-driven views, a `search` query string carrying its
  // filters/overlay); drive the router to it. Gated on the seq ADVANCING past what we've handled
  // (prevNavSeqRef, seeded to the seq at mount) so a persisted lastNav can't re-fire on remount
  // and a user/URL nav (which never bumps seq) can't trigger a router nav — no loop, no
  // mount/load race. We navigate only when the target URL differs from where we already are: a
  // directive onto the current view+query is a no-op, but a same-view REFINE (findings→findings
  // with new filters) still applies because the query differs.
  const prevNavSeqRef = useRef(useWorkspaceStore.getState().lastNav?.seq ?? 0);
  useEffect(() => {
    if (!lastNav || lastNav.seq === prevNavSeqRef.current) return;
    // Don't CONSUME the request until the catalog has resolved: a directive that arrives mid-load
    // (resolvedCatalogId still undefined) would otherwise be marked handled here and then dropped
    // when the id lands (the seq now matches prevNavSeqRef). Deferring only the *unresolved* window
    // keeps the eager consume-on-mismatch below — so a directive for another catalog is still
    // consumed (not left to re-fire when the user switches back to it).
    if (!resolvedCatalogId) return;
    prevNavSeqRef.current = lastNav.seq;
    if (lastNav.catalogId !== resolvedCatalogId) return;
    const target = `/catalogs/${resolvedCatalogId}/${canvasViewPath(lastNav.view)}${lastNav.search ? `?${lastNav.search}` : ''}`;
    if (target !== `${location.pathname}${location.search}`) navigate(target);
  }, [lastNav, resolvedCatalogId, navigate, location.pathname, location.search]);

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spinner size={32} />
      </div>
    );
  }

  if (!active) return <Navigate to="/welcome" replace />;

  // :id authoritative (SC-2457): an unknown catalog id can't resolve, so `active`
  // fell back to the user's default catalog — send the browser to that catalog's
  // canonical URL rather than render its data under a lying id. A KNOWN id needs
  // no redirect: useActiveCatalog syncs it into the store.
  const knownId = catalogs?.some((c) => c.id === id) ?? false;
  if (id && !knownId) return <Navigate to={`/catalogs/${active.id}/entities`} replace />;

  const context: CatalogWorkspaceContext = { active };

  // The entities section's nav label = the catalog's entity term, capitalized
  // ("products" → "Products", "athletes" → "Athletes").
  const entityTerm = entityPlural(active);
  const entityLabel = entityTerm.charAt(0).toUpperCase() + entityTerm.slice(1);

  const body = (
    <Suspense
      fallback={
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '40px',
          }}
        >
          <Spinner size={28} />
        </div>
      }
    >
      <Outlet context={context} />
    </Suspense>
  );

  // The workspace frame (SC-2655/SC-2659 → SC-2671): canvas ∣ ChatPanel, with the
  // section nav as top tabs inside the canvas (the app-level icon rail sits to the
  // left, from App). A full-height, edge-to-edge frame (no page padding — the chat
  // is a permanent in-flow column) in which each column scrolls on its own.
  return (
    <div style={{ height: '100%', fontFamily: FONT }}>
      <WorkspaceShell
        activeView={activeView}
        entityLabel={entityLabel}
        verticalLabel={active.vertical_label ?? null}
        catalogId={active.id}
        onNavigate={(view) => navigate(`/catalogs/${active.id}/${canvasViewPath(view)}`)}
        provenance={provenance}
        onClearProvenance={() => useWorkspaceStore.getState().clearProvenance(active.id)}
      >
        {body}
      </WorkspaceShell>
    </div>
  );
}
